/**
 * Triage metadata — single source of truth for type/category labels,
 * Chip variants, and the priority ordering. Mirrors the Python
 * constants in middleware/app/models/maildesk_triage.py.
 *
 * If you change the letter/category values here, mirror the change
 * server-side and re-deploy in lockstep.
 */
import type {
  AssistantStatus,
  Category,
  EmailType,
  InboxName,
  TriageStatus,
} from '../api/types'

export const EMAIL_TYPES: EmailType[] = ['F', 'A', 'B', 'D', 'E', 'G', 'H', 'C']

export const TYPE_LABEL: Record<EmailType, string> = {
  F: 'Urgent',
  A: 'New PO',
  B: 'Follow-Up',
  D: 'Portal',
  E: 'Inquiry',
  G: 'Sample',
  H: 'Admin',
  C: 'Suppressed',
}

export const TYPE_PRIORITY: Record<EmailType, number> = {
  F: 0,
  A: 1,
  B: 2,
  D: 3,
  E: 4,
  G: 5,
  H: 6,
  C: 6,
}

/** Chip variant per type for the filter bar + card badge. */
export const TYPE_VARIANT: Record<
  EmailType,
  'danger' | 'warning' | 'info' | 'neutral' | 'success' | 'accent'
> = {
  F: 'danger',
  A: 'info',
  B: 'warning',
  D: 'neutral',
  E: 'neutral',
  G: 'info',
  H: 'neutral',
  C: 'neutral',
}

export const CATEGORY_LABEL: Record<Category, string> = {
  new_po: 'New PO',
  po_revision: 'PO revision',
  sample_request: 'Sample request',
  cofa_request: 'CofA request',
  qa_escalation: 'QA escalation',
  logistics: 'Logistics',
  pricing_inquiry: 'Pricing inquiry',
  new_business: 'New business',
  accounting: 'Accounting',
  customer_system: 'Customer system',
  spam: 'Spam',
  unclassified: 'Unclassified',
}

export const STATUS_LABEL: Record<TriageStatus, string> = {
  open: 'Open',
  snoozed: 'Snoozed',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
  suppressed: 'Suppressed',
}

/** Chip variant per status — mirrors the Chip primitive's allowed variants. */
export const STATUS_VARIANT: Record<
  TriageStatus,
  'success' | 'warning' | 'info' | 'neutral' | 'danger' | 'accent'
> = {
  open: 'info',
  snoozed: 'warning',
  resolved: 'success',
  dismissed: 'neutral',
  suppressed: 'neutral',
}

export const INBOX_TABS: { value: InboxName | 'all'; label: string }[] = [
  { value: 'orders', label: 'Orders' },
  { value: 'purchasing', label: 'Purchasing' },
  { value: 'support', label: 'Support' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'all', label: 'All' },
]

export const INBOX_LABEL: Record<InboxName, string> = {
  orders: 'Orders',
  purchasing: 'Purchasing',
  support: 'Support',
  regulatory: 'Regulatory',
}

// ─── CARLA inbox-assistant (Sprint 35 chunks 5–8) ────────────────────

/**
 * Per-row status the Orders / Support agent writes when it finishes.
 * The TriageActionCard chip renders only when assistant_processed_at
 * is non-null — `pending` is a transient state that should rarely be
 * observable in the UI.
 */
export const ASSISTANT_STATUS_LABEL: Record<AssistantStatus, string> = {
  pending: 'CARLA queued',
  handled: 'CARLA handled',
  skipped: 'CARLA skipped',
  escalated: 'CARLA escalated',
  failed: 'CARLA failed',
}

export const ASSISTANT_STATUS_VARIANT: Record<
  AssistantStatus,
  'success' | 'warning' | 'info' | 'neutral' | 'danger'
> = {
  pending: 'info',
  handled: 'success',
  skipped: 'neutral',
  escalated: 'warning',
  failed: 'danger',
}

/** Sort items: priority asc, then latest_msg_at desc (nulls last). */
export function compareTriageItems<
  T extends { priority: number; latest_msg_at: string | null },
>(a: T, b: T): number {
  if (a.priority !== b.priority) return a.priority - b.priority
  const aT = a.latest_msg_at ? Date.parse(a.latest_msg_at) : 0
  const bT = b.latest_msg_at ? Date.parse(b.latest_msg_at) : 0
  return bT - aT
}
